import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Icon Button component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 149:34645):
 * - Sizes: sm (24px), md (32px), lg (36px)
 * - Types: Primary, Secondary, Tertiary, Plain, Danger, Destructive, Dashed
 * - States: Normal, Hover, Pressed, Focused, Disabled
 */
const iconButtonVariants = cva(
  'inline-flex items-center justify-center shrink-0 transition-all duration-100 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-grey-950 text-white shadow-[0px_0px_0px_1px_#121212] hover:bg-grey-900 active:bg-grey-950 focus:shadow-[0px_0px_0px_4px_rgba(18,18,18,0.2),0px_0px_0px_1px_#121212]',
        secondary:
          'bg-white text-grey-950 shadow-[0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.07),0px_1px_3px_0px_rgba(18,18,18,0.1)] hover:bg-grey-50 hover:shadow-[0px_1px_1px_0px_rgba(18,18,18,0.2),0px_0px_0px_1px_rgba(18,18,18,0.1),0px_1px_3px_0px_rgba(18,18,18,0.1)] active:bg-grey-100 focus:shadow-[0px_0px_0px_4px_rgba(18,18,18,0.1),0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.07),0px_1px_3px_0px_rgba(18,18,18,0.1)]',
        tertiary:
          'bg-black/5 text-grey-950 hover:bg-black/10 active:bg-black/15 focus:shadow-[0px_0px_0px_4px_rgba(11,19,36,0.1),0px_0px_0px_1px_rgba(18,18,18,0.07)]',
        plain:
          'text-grey-950 hover:bg-black/5 active:bg-black/10 focus:shadow-[0px_0px_0px_4px_rgba(11,19,36,0.1),0px_0px_0px_1px_rgba(18,18,18,0.07)]',
        danger:
          'bg-white text-red-600 shadow-[0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.07),0px_1px_3px_0px_rgba(18,18,18,0.1)] hover:bg-grey-50 active:bg-grey-100 focus:shadow-[0px_0px_0px_4px_rgba(11,19,36,0.1),0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.07),0px_1px_3px_0px_rgba(18,18,18,0.1)]',
        destructive:
          'bg-red-600 text-white shadow-[0px_0px_0px_1px_#aa2e26] hover:bg-red-700 active:bg-red-600 focus:shadow-[0px_0px_0px_4px_rgba(18,18,18,0.2),0px_0px_0px_1px_#aa2e26]',
        dashed:
          'bg-white text-grey-950 border border-dashed border-black/40 hover:bg-grey-50 hover:border-black/40 active:bg-grey-100 focus:border-black/40',
        ghost:
          'text-grey-500 hover:text-grey-950 hover:bg-black/5 active:bg-black/10',
      },
      size: {
        sm: 'h-6 w-6 rounded-md',
        md: 'h-8 w-8 rounded-lg',
        lg: 'h-9 w-9 rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'md',
    },
  }
);

const IconButton = React.forwardRef(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(iconButtonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
IconButton.displayName = 'IconButton';

export { IconButton, iconButtonVariants };
